import { isBattleBadge } from 'features/catalog/badge-definitions';
import { CPU_DIFFICULTIES, CPU_DIFFICULTY_LABELS, type CpuDifficulty } from 'features/battle/ai-config';
import {
  CUSTOM_BATTLE_DECK,
  DECK_OF_THE_DAY,
  SAMPLE_BATTLE_DECKS,
  type BattleDeckSource,
} from 'features/battle/sample-decks';
import { BATTLE_TUTORIAL } from 'features/battle/tutorial';
import { isTutorialDone } from 'features/battle/tutorial-progress';
import { BATTLE_DECK_SIZE } from 'features/battle/types';
import { isJsonObject, parseJson, type Json } from 'shared/lib/json';

const STORAGE_KEY = 'tb-battle-saved-decks';
const YOUR_DECK_KEY = 'tb-battle-your-deck';
const DECK_SOURCE_KEY = 'tb-battle-deck-source';
const CPU_DIFFICULTY_KEY = 'tb-battle-cpu-difficulty';
const CPU_WINS_KEYS = {
  classic: 'tb-battle-cpu-wins',
  arena: 'tb-arena-cpu-wins',
} as const;
const CHALLENGE_MODE_KEY = 'tb-battle-challenge-mode';
const RANDOMIZE_MODE_KEY = 'tb-battle-randomize-mode';
const CLAIMED_ONLY_KEY = 'tb-battle-claimed-only';
const MAX_NAME_LENGTH = 48;

/** Which badges an action may use. `pure` limits it to badges the player has claimed. */
export type BattlePureMode = 'any' | 'pure';

export const BATTLE_PURE_MODES: readonly BattlePureMode[] = ['any', 'pure'];

export const BATTLE_PURE_MODE_LABELS: Record<BattlePureMode, string> = {
  any: 'Any Badges',
  pure: 'Claimed Only',
};

export type SavedBattleDeck = {
  id: string;
  name: string;
  badgeIds: string[];
  savedAt: number;
};

export const formatBattleDeckCode = (badgeIds: readonly string[]): string => badgeIds.join(',');

export const parseBattleDeckCode = (raw: string): string[] => {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const match of raw.matchAll(/[a-z0-9]+/gi)) {
    const id = match[0].toLowerCase();
    if (seen.has(id) || !isBattleBadge(id)) continue;
    seen.add(id);
    ids.push(id);
    if (ids.length === BATTLE_DECK_SIZE) break;
  }
  return ids;
};

export const deckIdsMatch = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) return false;
  const other = new Set(b);
  return other.size === a.length && a.every((id) => other.has(id));
};

export const normalizeSavedDeckName = (name: string): string =>
  name.trim().replace(/\s+/g, ' ').slice(0, MAX_NAME_LENGTH);

const newId = (): string => `bd-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;

const asDeckIds = (value: Json | readonly string[]): string[] | null => {
  if (!Array.isArray(value) || value.length !== BATTLE_DECK_SIZE) return null;
  const ids: string[] = [];
  for (const id of value) {
    if (typeof id !== 'string' || !id || !isBattleBadge(id)) return null;
    ids.push(id);
  }
  if (new Set(ids).size !== BATTLE_DECK_SIZE) return null;
  return ids;
};

const asSavedDeck = (value: Json): SavedBattleDeck | null => {
  if (!isJsonObject(value)) return null;
  const name = typeof value.name === 'string' ? normalizeSavedDeckName(value.name) : '';
  const badgeIds = asDeckIds(value.badgeIds);
  const id = typeof value.id === 'string' && value.id ? value.id : null;
  const savedAt = typeof value.savedAt === 'number' && Number.isFinite(value.savedAt) ? value.savedAt : null;
  if (!name || !badgeIds || !id || savedAt == null) return null;
  return { id, name, badgeIds, savedAt };
};

const readAll = (): SavedBattleDeck[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = parseJson(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(asSavedDeck).filter((deck): deck is SavedBattleDeck => Boolean(deck));
  } catch {
    return [];
  }
};

const writeAll = (decks: readonly SavedBattleDeck[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(decks));
  } catch {
    /* ignore quota / private mode */
  }
};

export const listSavedBattleDecks = (): SavedBattleDeck[] => [...readAll()].sort((a, b) => b.savedAt - a.savedAt);

/** Last cards placed in Your Deck. `null` means nothing has been stored on this device yet. */
const asYourDeckIds = (value: Json | readonly string[]): string[] | null => {
  if (!Array.isArray(value)) return null;
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const entry of value) {
    if (typeof entry !== 'string' || !entry || seen.has(entry) || !isBattleBadge(entry)) continue;
    seen.add(entry);
    ids.push(entry);
  }
  return ids.slice(-BATTLE_DECK_SIZE);
};

export const readYourBattleDeck = (): string[] | null => {
  try {
    const raw = localStorage.getItem(YOUR_DECK_KEY);
    if (raw == null) return null;
    return asYourDeckIds(parseJson(raw));
  } catch {
    return null;
  }
};

export const writeYourBattleDeck = (badgeIds: readonly string[]): void => {
  const ids = asYourDeckIds([...badgeIds]) ?? [];
  try {
    localStorage.setItem(YOUR_DECK_KEY, JSON.stringify(ids));
  } catch {
    /* ignore quota / private mode */
  }
};

const asBattleDeckSource = (value: string | null): BattleDeckSource => {
  if (!value || value === CUSTOM_BATTLE_DECK) return CUSTOM_BATTLE_DECK;
  if (value === DECK_OF_THE_DAY) return DECK_OF_THE_DAY;
  if (SAMPLE_BATTLE_DECKS.some((deck) => deck.id === value)) return value as BattleDeckSource;
  return CUSTOM_BATTLE_DECK;
};

/** Last deck picker choice. */
export const readBattleDeckSource = (): BattleDeckSource => {
  try {
    return asBattleDeckSource(localStorage.getItem(DECK_SOURCE_KEY));
  } catch {
    return CUSTOM_BATTLE_DECK;
  }
};

export const writeBattleDeckSource = (source: BattleDeckSource): void => {
  try {
    localStorage.setItem(DECK_SOURCE_KEY, asBattleDeckSource(source));
  } catch {
    /* ignore quota / private mode */
  }
};

export type CpuUnlockMode = 'classic' | 'arena';

/** Beat this difficulty to unlock the next one. Easy is always unlocked. */
const CPU_DIFFICULTY_UNLOCKS: Record<CpuDifficulty, CpuDifficulty | null> = {
  easy: null,
  hard: 'easy',
  impossible: 'hard',
};

const CPU_UNLOCK_HINT = {
  classic: 'Win a battle against the CPU in',
  arena: 'Win an Arena against the CPU in',
} as const;

const asCpuDifficulty = (value: Json | string | null): CpuDifficulty | null =>
  typeof value === 'string' ? (CPU_DIFFICULTIES.find((level) => level === value) ?? null) : null;

const seedBeatenFromStoredDifficulty = (): CpuDifficulty[] => {
  try {
    const stored = asCpuDifficulty(localStorage.getItem(CPU_DIFFICULTY_KEY));
    if (stored === 'impossible') return ['easy', 'hard'];
    if (stored === 'hard') return ['easy'];
  } catch {
    /* ignore */
  }
  return [];
};

const readBeatenCpuDifficulties = (mode: CpuUnlockMode): Set<CpuDifficulty> => {
  try {
    const key = CPU_WINS_KEYS[mode];
    const raw = localStorage.getItem(key);
    if (raw == null) {
      const seeded = mode === 'classic' ? seedBeatenFromStoredDifficulty() : [];
      localStorage.setItem(key, JSON.stringify(seeded));
      return new Set(seeded);
    }
    const parsed = parseJson(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map(asCpuDifficulty).filter((level): level is CpuDifficulty => Boolean(level)));
  } catch {
    return new Set();
  }
};

export const isCpuDifficultyUnlocked = (difficulty: CpuDifficulty, mode: CpuUnlockMode = 'classic'): boolean => {
  const need = CPU_DIFFICULTY_UNLOCKS[difficulty];
  if (need == null || readBeatenCpuDifficulties(mode).has(need)) return true;
  // Beating the Hard tutorial CPU unlocks Hard in Battle and Arena.
  return need === 'easy' && isTutorialDone(BATTLE_TUTORIAL.id);
};

export const cpuDifficultyUnlockHint = (difficulty: CpuDifficulty, mode: CpuUnlockMode = 'classic'): string | null => {
  const need = CPU_DIFFICULTY_UNLOCKS[difficulty];
  if (!need || isCpuDifficultyUnlocked(difficulty, mode)) return null;
  return `${CPU_UNLOCK_HINT[mode]} ${CPU_DIFFICULTY_LABELS[need]} first`;
};

const writeBeatenCpuDifficulties = (mode: CpuUnlockMode, beaten: Iterable<CpuDifficulty>): void => {
  try {
    localStorage.setItem(CPU_WINS_KEYS[mode], JSON.stringify([...beaten]));
  } catch {
    /* ignore quota / private mode */
  }
};

export const recordCpuDifficultyWin = (difficulty: CpuDifficulty, mode: CpuUnlockMode = 'classic'): void => {
  const beaten = readBeatenCpuDifficulties(mode);
  if (beaten.has(difficulty)) return;
  beaten.add(difficulty);
  writeBeatenCpuDifficulties(mode, beaten);
};

/** Dev / reset: Easy only — Hard and Impossible lock again. */
export const resetCpuDifficultyWins = (): void => {
  writeBeatenCpuDifficulties('classic', []);
  writeBeatenCpuDifficulties('arena', []);
};

/** Dev: beat Easy and Hard in both modes so Impossible is playable. */
export const unlockAllCpuDifficulties = (): void => {
  for (const mode of ['classic', 'arena'] as const) {
    const beaten = readBeatenCpuDifficulties(mode);
    beaten.add('easy');
    beaten.add('hard');
    writeBeatenCpuDifficulties(mode, beaten);
  }
};

const highestUnlockedCpuDifficulty = (preferred: CpuDifficulty): CpuDifficulty => {
  const index = CPU_DIFFICULTIES.indexOf(preferred);
  for (let i = index; i >= 0; i--) {
    const level = CPU_DIFFICULTIES[i];
    if (isCpuDifficultyUnlocked(level)) return level;
  }
  return 'easy';
};

export const readCpuDifficulty = (): CpuDifficulty => {
  try {
    const stored = asCpuDifficulty(localStorage.getItem(CPU_DIFFICULTY_KEY)) ?? 'easy';
    return highestUnlockedCpuDifficulty(stored);
  } catch {
    return 'easy';
  }
};

export const writeCpuDifficulty = (difficulty: CpuDifficulty): void => {
  if (!isCpuDifficultyUnlocked(difficulty)) return;
  try {
    localStorage.setItem(CPU_DIFFICULTY_KEY, difficulty);
  } catch {
    /* ignore quota / private mode */
  }
};

const readPureMode = (key: string): BattlePureMode => {
  try {
    const raw = localStorage.getItem(key);
    return BATTLE_PURE_MODES.find((mode) => mode === raw) ?? 'any';
  } catch {
    return 'any';
  }
};

const writePureMode = (key: string, mode: BattlePureMode): void => {
  try {
    localStorage.setItem(key, mode);
  } catch {
    /* ignore quota / private mode */
  }
};

export const readBattleChallengeMode = (): BattlePureMode => readPureMode(CHALLENGE_MODE_KEY);
export const writeBattleChallengeMode = (mode: BattlePureMode): void => writePureMode(CHALLENGE_MODE_KEY, mode);
export const readBattleRandomizeMode = (): BattlePureMode => readPureMode(RANDOMIZE_MODE_KEY);
export const writeBattleRandomizeMode = (mode: BattlePureMode): void => writePureMode(RANDOMIZE_MODE_KEY, mode);

export const readBattleClaimedOnly = (): boolean => {
  try {
    return localStorage.getItem(CLAIMED_ONLY_KEY) === 'true';
  } catch {
    return false;
  }
};

export const writeBattleClaimedOnly = (on: boolean): void => {
  try {
    localStorage.setItem(CLAIMED_ONLY_KEY, on ? 'true' : 'false');
  } catch {
    /* ignore quota / private mode */
  }
};

export const saveBattleDeck = (name: string, badgeIds: readonly string[]): SavedBattleDeck | null => {
  const normalized = normalizeSavedDeckName(name);
  const ids = asDeckIds(badgeIds);
  if (!normalized || !ids) return null;
  const now = Date.now();
  const current = readAll();
  const existingIndex = current.findIndex((deck) => deck.name.toLowerCase() === normalized.toLowerCase());
  const saved: SavedBattleDeck =
    existingIndex >= 0
      ? { ...current[existingIndex], name: normalized, badgeIds: ids, savedAt: now }
      : { id: newId(), name: normalized, badgeIds: ids, savedAt: now };
  const next =
    existingIndex >= 0 ? current.map((deck, index) => (index === existingIndex ? saved : deck)) : [saved, ...current];
  writeAll(next);
  return saved;
};

export const deleteSavedBattleDeck = (id: string): SavedBattleDeck[] => {
  writeAll(readAll().filter((deck) => deck.id !== id));
  return listSavedBattleDecks();
};
